import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthentication } from "nikao-authentication";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Person,
  Lock,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";

const Login = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithToken } = useAuth();
  // useAuthentication hook inherits redirectUri from AuthenticationProvider in App.js
  const msalClientId = process.env.REACT_APP_MSAL_CLIENT_ID || '';
  const { login: msalLogin, getIdToken } = useAuthentication({
    scopes: [msalClientId + '/.default'],
  });

  // Handle popup redirect - if we're in a popup with an auth code, process it
  useEffect(() => {
    // Check if we're in a popup window and have an auth code in the URL
    const isPopup = window.opener !== null;
    const hash = window.location.hash;

    if (isPopup && hash) {
      // We're in a popup with an auth code - try to get the token
      const processPopupAuth = async () => {
        try {
          setLoading(true);
          // Wait a bit for the library to process the code
          await new Promise((resolve) => setTimeout(resolve, 500));

          const token = getIdToken();
          if (token) {
            // Send token to backend
            const result = await loginWithToken(token);
            if (result.success) {
              // Close the popup and notify parent window
              window.opener?.postMessage(
                { type: "MSAL_AUTH_SUCCESS", token },
                window.location.origin,
              );
              window.close();
            } else {
              setError(result.message);
              window.opener?.postMessage(
                { type: "MSAL_AUTH_ERROR", error: result.message },
                window.location.origin,
              );
            }
          } else {
            // No token yet, might need to wait longer or there's an error
            console.warn("No token available in popup redirect");
          }
        } catch (err) {
          console.error("Popup auth processing error:", err);
          window.opener?.postMessage(
            { type: "MSAL_AUTH_ERROR", error: err.message },
            window.location.origin,
          );
        } finally {
          setLoading(false);
        }
      };

      processPopupAuth();
    }
  }, [getIdToken, loginWithToken]);

  // Get the return URL or default to dashboard
  const from = location.state?.from?.pathname || "/";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(""); // Clear error when user starts typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await login(formData);

      if (result.success) {
        toast.success(`Welcome back, ${formData.username}!`);
        navigate(from, { replace: true });
      } else {
        setError(result.message);
        toast.error(result.message);
      }
    } catch (error) {
      const errorMessage = "An unexpected error occurred. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Listen for messages from popup window
  useEffect(() => {
    const handleMessage = async (event) => {
      // Verify message is from same origin
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "MSAL_AUTH_SUCCESS") {
        // Popup successfully authenticated
        const token = event.data.token || getIdToken();
        if (token) {
          const result = await loginWithToken(token);
          if (result.success) {
            toast.success("Welcome!");
            navigate(from, { replace: true });
          } else {
            setError(result.message);
            toast.error(result.message);
          }
        }
      } else if (event.data.type === "MSAL_AUTH_ERROR") {
        setError(event.data.error || "Sign in with Microsoft failed.");
        toast.error(event.data.error || "Sign in with Microsoft failed.");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [getIdToken, loginWithToken, navigate, from]);

  const handleMicrosoftSignIn = async () => {
    setLoading(true);
    setError("");
    try {
      await msalLogin(); // Opens Microsoft sign-in
      const token = getIdToken(); // Entra ID JWT
      if (!token) {
        setError("Could not get Microsoft sign-in token.");
        toast.error("Could not get Microsoft sign-in token.");
        return;
      }
      // Send token to backend via POST /api/auth/login/token and store the session/JWT we get back
      const result = await loginWithToken(token);
      if (result.success) {
        toast.success("Welcome!");
        navigate(from, { replace: true });
      } else {
        setError(result.message);
        toast.error(result.message);
      }
    } catch (err) {
      const message =
        err.message || "Sign in with Microsoft failed. Please try again.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            maxWidth: 400,
          }}
        >
          {/* Logo and Title */}
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <LoginIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              Nikao Profile
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Sign in to your account
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleInputChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading || !formData.username || !formData.password}
              startIcon={
                loading ? <CircularProgress size={20} /> : <LoginIcon />
              }
            >
              {loading ? "Signing In..." : "Sign In"}
            </Button>

            <Button
              type="button"
              fullWidth
              variant="outlined"
              sx={{ mt: 1, mb: 2, py: 1.5 }}
              disabled={loading}
              onClick={handleMicrosoftSignIn}
            >
              Sign in with Microsoft
            </Button>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Nikao Technology. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default Login;
